import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, AlertTriangle, Info, ShieldAlert, CheckCircle2, FileEdit } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import notificationService from '../../services/notification/notificationService';
import { NOTIFICATION_PRIORITY, NOTIFICATION_TYPES } from '../../config/constants';
import DOMPurify from 'dompurify';

const getPriorityColor = (priority) => {
  switch (priority) {
    case NOTIFICATION_PRIORITY.URGENT: return 'text-red-500 bg-red-500/10 border-red-500/20';
    case NOTIFICATION_PRIORITY.HIGH: return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
    case NOTIFICATION_PRIORITY.MEDIUM: return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    case NOTIFICATION_PRIORITY.LOW:
    default: return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
  }
};

const getIcon = (type, priority) => {
  if (priority === NOTIFICATION_PRIORITY.URGENT) return <ShieldAlert size={16} />;
  if (priority === NOTIFICATION_PRIORITY.HIGH) return <AlertTriangle size={16} />;
  
  switch (type) {
    case NOTIFICATION_TYPES.SUCCESS:
    case NOTIFICATION_TYPES.MARK_SUBMITTED:
    case NOTIFICATION_TYPES.MARK_UNLOCK_APPROVED:
      return <CheckCircle2 size={16} />;
    case NOTIFICATION_TYPES.WARNING:
    case NOTIFICATION_TYPES.MARK_DEADLINE:
      return <AlertTriangle size={16} />;
    case NOTIFICATION_TYPES.NOTICE:
    case NOTIFICATION_TYPES.MARK_UNLOCK_REQUEST:
      return <FileEdit size={16} />;
    default:
      return <Info size={16} />;
  }
};

export default function NotificationBell() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!userProfile) return;
    
    // Subscribe to unread notifications (polls every 30s)
    const unsubscribe = notificationService.subscribeToUnreadNotifications(userProfile, (data) => {
      setNotifications(data);
    }, 30000);

    return () => unsubscribe();
  }, [userProfile]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAllRead = async () => {
    if (!userProfile || notifications.length === 0) return;
    try {
      await notificationService.markAllAsRead(userProfile, notifications);
      setNotifications([]);
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      await notificationService.markAsRead(notif, userProfile.uid);
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setIsOpen(false);
      
      // Navigate to full notifications page or specific modal
      navigate('/notifications');
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const unreadCount = notifications.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-white transition-colors rounded-full hover:bg-white/5"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-slate-900">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-slate-800 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 flex flex-col max-h-[80vh]">
          <div className="p-4 border-b border-white/10 flex items-center justify-between bg-slate-800/95 backdrop-blur z-10 sticky top-0">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllRead}
                className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
              >
                <Check size={14} /> Mark all read
              </button>
            )}
          </div>
          
          <div className="overflow-y-auto scrollbar-thin flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500 flex flex-col items-center gap-2">
                <Bell size={32} className="opacity-20" />
                <p className="text-sm">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {notifications.slice(0, 20).map((notif) => (
                  <div 
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className="p-4 hover:bg-white/5 cursor-pointer transition-colors relative pl-4 border-l-2 border-teal-500"
                  >
                    <div className="flex gap-3">
                      <div className={`mt-1 p-1.5 rounded-lg border shrink-0 ${getPriorityColor(notif.priority)}`}>
                        {getIcon(notif.type, notif.priority)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-white mb-1 leading-tight break-words" style={{ fontFamily: "'SolaimanLipi', sans-serif" }}>
                          {notif.title}
                        </h4>
                        <div 
                          className="text-xs text-slate-400 line-clamp-2 leading-relaxed" 
                          style={{ fontFamily: "'SolaimanLipi', sans-serif" }}
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notif.shortDescription || notif.content || '') }}
                        />
                        <span className="text-[10px] text-slate-500 mt-2 block">
                          {new Date(notif.createdAt).toLocaleString('en-US', { 
                            timeZone: 'Asia/Dhaka',
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="p-3 border-t border-white/10 bg-slate-800/95 sticky bottom-0">
            <button 
              onClick={() => { setIsOpen(false); navigate('/notifications'); }}
              className="w-full py-2 text-sm text-center text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
